import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import {
  CheckCircle2, AlertTriangle, Loader2, FileText, Building2,
} from "lucide-react";
import { sanitizeRichTextHtml } from "@/lib/sanitizeHtml";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

interface PublicProposal {
  id: number;
  title: string;
  status: string;
  intro: string;
  services: string;
  pricing: string;
  timeline: string;
  nextSteps: string;
  acceptedAt: string | null;
  signedAt: string | null;
  signerName: string | null;
  signerTitle: string | null;
  viewedAt: string | null;
  sentAt: string | null;
  opportunity: { name: string; company: string | null } | null;
}

interface AcceptError {
  status: number;
  error: string;
  signerName?: string | null;
  signerTitle?: string | null;
  acceptedAt?: string | null;
}

const SECTIONS: Array<{ key: keyof PublicProposal; label: string; emphasize?: boolean }> = [
  { key: "intro",     label: "Introduction" },
  { key: "services",  label: "Services" },
  { key: "pricing",   label: "Pricing", emphasize: true },
  { key: "timeline",  label: "Timeline" },
  { key: "nextSteps", label: "Next steps" },
];

function isEmptyHtml(html: string | undefined | null): boolean {
  if (!html) return true;
  const stripped = html.replace(/<[^>]+>/g, "").trim();
  return stripped.length === 0;
}

export default function ProposalView() {
  const [, params] = useRoute("/proposals/:token");
  const token = params?.token ?? "";

  const { data, isLoading, isError, refetch } = useQuery<PublicProposal>({
    queryKey: ["public-proposal", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/proposals/${encodeURIComponent(token)}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error("not_found");
        throw new Error(`Fetch failed (${res.status})`);
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  // Fire-and-forget view-recording on first successful load
  useEffect(() => {
    if (!data || !token) return;
    fetch(`/api/public/proposals/${encodeURIComponent(token)}/view`, { method: "POST" }).catch(() => {
      // silent — view tracking is best-effort
    });
  }, [data, token]);

  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [acceptError, setAcceptError] = useState<AcceptError | null>(null);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/proposals/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signerName, signerTitle: signerTitle || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw { status: res.status, ...body } as AcceptError;
      }
      return body;
    },
    onSuccess: () => {
      setAcceptError(null);
      refetch();
    },
    onError: (err: any) => {
      setAcceptError({ status: err?.status ?? 0, error: err?.error ?? "Something went wrong. Please try again.", ...err });
    },
  });

  if (!token) {
    return <Centered>Missing proposal token.</Centered>;
  }
  if (isLoading) {
    return <Centered><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading proposal…</Centered>;
  }
  if (isError) {
    return (
      <Centered>
        <FileText className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-lg font-medium">Proposal not found</p>
        <p className="text-sm text-muted-foreground mt-1">The link may have expired or been revoked.</p>
      </Centered>
    );
  }
  if (!data) return null;

  const isAccepted = !!data.acceptedAt;
  const isExpired = data.status === "expired";
  const isDeclined = data.status === "declined";

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <FileText className="h-3.5 w-3.5" />
            Proposal · Prepared {data.sentAt ? new Date(data.sentAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}
          </div>
          <h1 className="text-3xl font-semibold">{data.title}</h1>
          {data.opportunity && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              Prepared for {data.opportunity.name}
              {data.opportunity.company && ` · ${data.opportunity.company}`}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6 space-y-6">
        {SECTIONS.map((section) => {
          const value = data[section.key] as string;
          if (isEmptyHtml(value)) return null;
          return (
            <Card key={section.key} className={section.emphasize ? "border-primary/40 ring-1 ring-primary/10" : undefined}>
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-3">{section.label}</h2>
                <div
                  className="prose prose-sm sm:prose-base max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(value) }}
                />
              </CardContent>
            </Card>
          );
        })}

        {/* Acceptance block */}
        <Card className={isAccepted ? "bg-emerald-50 border-emerald-200" : "bg-background"}>
          <CardContent className="pt-6">
            {isAccepted ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-medium">This proposal was accepted on {new Date(data.acceptedAt!).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    By {data.signerName}{data.signerTitle ? `, ${data.signerTitle}` : ""}.
                  </p>
                </div>
              </div>
            ) : isDeclined ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
                <div>
                  <p className="font-medium">This proposal was declined.</p>
                  <p className="text-sm text-muted-foreground mt-1">Contact your account manager if you'd like to revisit the terms.</p>
                </div>
              </div>
            ) : isExpired ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-muted-foreground shrink-0" />
                <div>
                  <p className="font-medium">This proposal has expired.</p>
                  <p className="text-sm text-muted-foreground mt-1">Reach out to your account manager for a refreshed proposal.</p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-1">Accept this proposal</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  By typing your full name below, you agree to the terms outlined above.
                </p>

                {acceptError && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-destructive">{acceptError.error}</p>
                      {acceptError.status === 409 && acceptError.signerName && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Originally signed by {acceptError.signerName}
                          {acceptError.signerTitle ? `, ${acceptError.signerTitle}` : ""}
                          {acceptError.acceptedAt && ` on ${new Date(acceptError.acceptedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}.
                        </p>
                      )}
                      <button
                        className="text-xs text-muted-foreground underline mt-1"
                        onClick={() => setAcceptError(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <Label>Your full name</Label>
                    <Input
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <Label>Title (optional)</Label>
                    <Input
                      value={signerTitle}
                      onChange={(e) => setSignerTitle(e.target.value)}
                      placeholder="CFO"
                    />
                  </div>
                  <Button
                    onClick={() => acceptMutation.mutate()}
                    disabled={!signerName.trim() || acceptMutation.isPending}
                    className="w-full"
                  >
                    {acceptMutation.isPending ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
                    ) : (
                      <>Accept proposal</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <footer className="text-center text-xs text-muted-foreground pt-4 pb-8">
          This is a private proposal. Do not share this link.
        </footer>
      </main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-muted/30">
      {children}
    </div>
  );
}
